import { gsap } from 'gsap';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';
import { SHAPES } from './backgroundBubbleUtils';

gsap.registerPlugin(MorphSVGPlugin);

export type MorphTimelineCache = Map<string, gsap.core.Timeline>;

const TRANSITION_AT = 0.48;
const normalizedPathCache = new Map<string, string>();

/** Normalise un path SVG pour MorphSVG (même structure de commandes). */
export function normalizePath(path: string): string {
  const cached = normalizedPathCache.get(path);
  if (cached) return cached;

  try {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const el = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    el.setAttribute('d', path);
    svg.appendChild(el);
    document.body.appendChild(svg);
    (MorphSVGPlugin as unknown as { convertToPath: (target: SVGPathElement) => void }).convertToPath(el);
    const normalized = el.getAttribute('d') ?? path;
    svg.remove();
    normalizedPathCache.set(path, normalized);
    return normalized;
  } catch {
    normalizedPathCache.set(path, path);
    return path;
  }
}

export function createMorphTimelineCache(): MorphTimelineCache {
  return new Map();
}

export function clearMorphTimelineCache(cache: MorphTimelineCache): void {
  cache.forEach((tl) => tl.kill());
  cache.clear();
}

function cacheKey(fromPath: string, toPath: string): string {
  return `${fromPath}|${toPath}`;
}

/**
 * Timeline pausée from → TRANSITION (35%) → to, scrubbable via progress(t).
 */
export function getSegmentMorphTimeline(
  pathEl: SVGPathElement,
  fromPath: string,
  toPath: string,
  cache: MorphTimelineCache,
  transitionPath: string = SHAPES.TRANSITION,
): gsap.core.Timeline {
  const normalizedFrom = normalizePath(fromPath);
  const normalizedTo = normalizePath(toPath);
  const normalizedTransition = normalizePath(transitionPath);
  const key = cacheKey(normalizedFrom, normalizedTo);
  const cached = cache.get(key);
  if (cached) return cached;

  gsap.set(pathEl, { attr: { d: normalizedFrom } });

  const tl = gsap.timeline({ paused: true, defaults: { ease: 'none' } });
  tl.to(pathEl, {
    morphSVG: normalizedTransition,
    duration: TRANSITION_AT,
  });
  tl.to(pathEl, {
    morphSVG: normalizedTo,
    duration: 1 - TRANSITION_AT,
  });

  cache.set(key, tl);
  return tl;
}

/** Applique le progress morph pour un segment ; pause les autres timelines du cache. */
export function applyMorphProgress(
  pathEl: SVGPathElement,
  fromPath: string,
  toPath: string,
  progress: number,
  cache: MorphTimelineCache,
  transitionPath: string = SHAPES.TRANSITION,
): void {
  const activeKey = cacheKey(normalizePath(fromPath), normalizePath(toPath));
  cache.forEach((tl, key) => {
    if (key !== activeKey) {
      tl.pause();
    }
  });

  const tl = getSegmentMorphTimeline(pathEl, fromPath, toPath, cache, transitionPath);
  tl.progress(Math.min(1, Math.max(0, progress)));
}
